import { useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { api } from '../api/client';
import { startSessionRecording, type SessionRecorder } from '../live/sessionRecorder';
import { Button, Dialog } from './ui';

const ackKey = (domain: string) => `stitch.recording.told.${domain.toLowerCase()}`;
function told(domain: string): boolean {
  try {
    return sessionStorage.getItem(ackKey(domain)) === '1';
  } catch {
    return false;
  }
}
function remember(domain: string): void {
  try {
    sessionStorage.setItem(ackKey(domain), '1');
  } catch {
    // Private mode: they're told again next time.
  }
}

/**
 * Whether this tab is being recorded right now, and for how long recordings are kept: what the
 * Recording chip shows (in the menu's top on a computer, in the top bar on a phone).
 */
let recorded: { days: number } | null = null;
const listeners = new Set<() => void>();
function setRecorded(next: { days: number } | null): void {
  if (recorded?.days === next?.days && Boolean(recorded) === Boolean(next)) return;
  recorded = next;
  for (const l of [...listeners]) l();
}
function useRecorded(): { days: number } | null {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => recorded,
    () => recorded
  );
}

/** What being recorded means, as people are told before it starts (and again from the chip). */
function WhatIsRecorded({ days }: { days: number }): ReactNode {
  return (
    <ul>
      <li>Only this portal is recorded, not the rest of your screen or other tabs.</li>
      <li>What you type is hidden, and passwords are never recorded.</li>
      <li>
        Recordings are kept for {days} {days === 1 ? 'day' : 'days'}, and only your company’s admins can watch them.
      </li>
    </ul>
  );
}

/**
 * A small "Recording" mark while this session is recorded: beside Stitch at the top of the menu on
 * a computer (`place="menu"`), in the top bar on a phone (`place="top"`). Choosing it says what's
 * recorded. Nothing shows when the session isn't recorded.
 */
export function RecordingChip({ place }: { place: 'menu' | 'top' }) {
  const rec = useRecorded();
  const [open, setOpen] = useState(false);
  if (!rec) return null;
  return (
    <>
      <button
        type="button"
        className={`rec-chip rec-chip-${place}`}
        aria-label="This session is recorded for your company’s admins. What that means"
        title={`This portal session is recorded for your company’s admins (kept ${rec.days} ${rec.days === 1 ? 'day' : 'days'}).`}
        onClick={() => setOpen(true)}
      >
        <span className="rec-chip-dot" aria-hidden="true" />
        <span className="rec-chip-label">Recording</span>
      </button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="This session is recorded"
        foot={
          <Button variant="primary" onClick={() => setOpen(false)}>
            OK
          </Button>
        }
      >
        <div className="rec-notice">
          <p>Your company’s admins record what happens on screen while you use the Stitch portal, so they can see how something was done.</p>
          <WhatIsRecorded days={rec.days} />
          <p className="muted small">Questions about this go to your company’s admin.</p>
        </div>
      </Dialog>
    </>
  );
}

/**
 * When a company records sessions for this person's role: they're told when they sign in (before
 * anything is recorded), and the Recording chip shows while it runs (RecordingChip).
 */
export function SessionRecording({ domain }: { domain: string }) {
  const [plan, setPlan] = useState<{ days: number } | null>(null);
  const [asking, setAsking] = useState(false);
  const [running, setRunning] = useState(false);
  const rec = useRef<SessionRecorder | null>(null);

  useEffect(() => {
    let gone = false;
    setPlan(null);
    api
      .recordingMe(domain)
      .then((r) => {
        if (gone || !r.record) return;
        setPlan({ days: r.retentionDays ?? 30 });
        if (told(domain)) setRunning(true);
        else setAsking(true);
      })
      .catch(() => undefined);
    return () => {
      gone = true;
    };
  }, [domain]);

  useEffect(() => {
    if (!running) return;
    rec.current = startSessionRecording(domain, () => setRunning(false));
    return () => {
      rec.current?.stop();
      rec.current = null;
    };
  }, [running, domain]);

  // The chip shows while it runs.
  const days = plan?.days;
  useEffect(() => {
    setRecorded(running && days !== undefined ? { days } : null);
    return () => setRecorded(null);
  }, [running, days]);

  if (!plan) return null;
  const ok = () => {
    remember(domain);
    setAsking(false);
    setRunning(true);
  };
  return (
    <>
      <Dialog
        open={asking}
        onClose={ok}
        title="Your company records portal sessions"
        foot={
          <Button variant="primary" onClick={ok}>
            OK, got it
          </Button>
        }
      >
        <div className="rec-notice">
          <p>Your company’s admins record what happens on screen while you use the Stitch portal, so they can see how something was done.</p>
          <WhatIsRecorded days={plan.days} />
          <p className="muted small">Questions about this go to your company’s admin. While it runs, “Recording” shows at the top of the menu (in the top bar on a phone).</p>
        </div>
      </Dialog>
    </>
  );
}
